import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageParts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { fmtDate, fmtMoney } from "@/lib/format";
import { toast } from "sonner";
import { Plus } from "lucide-react";

export default function Tax() {
  const [worksheets, setWorksheets] = useState([]);
  const [fx, setFx] = useState([]);
  const [openSST, setOpenSST] = useState(false);
  const [openFX, setOpenFX] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const [w, f] = await Promise.all([api.get("/tax/sst"), api.get("/tax/fx")]);
    setWorksheets(w.data); setFx(f.data);
  };
  useEffect(() => { load(); }, []);

  return (
    <>
      <PageHeader title="SST & FX" subtitle="Prepare SST worksheets from verified collections. Log BNM FX rates for reporting." />
      <PageBody>
        <Tabs defaultValue="sst">
          <TabsList className="mb-4">
            <TabsTrigger value="sst" data-testid="tab-sst">SST worksheets</TabsTrigger>
            <TabsTrigger value="fx" data-testid="tab-fx">FX rates</TabsTrigger>
          </TabsList>
          <TabsContent value="sst">
            <div className="flex justify-end mb-3"><Button size="sm" onClick={() => setOpenSST(true)} data-testid="new-sst-button"><Plus className="w-4 h-4 mr-1.5" /> New worksheet</Button></div>
            <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Period</TableHead><TableHead>Filing due</TableHead><TableHead>Payment due</TableHead><TableHead className="text-right">Gross</TableHead><TableHead className="text-right">Tax</TableHead><TableHead className="text-right">Lines</TableHead></TableRow></TableHeader>
                <TableBody>
                  {worksheets.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-6">No worksheets.</TableCell></TableRow>}
                  {worksheets.map((w) => (
                    <TableRow key={w.id} className="cursor-pointer" onClick={() => setSelected(w)} data-testid={`sst-row-${w.id}`}>
                      <TableCell className="text-xs font-mono">{w.period_start} → {w.period_end}</TableCell>
                      <TableCell className="text-xs font-mono">{w.filing_deadline}</TableCell>
                      <TableCell className="text-xs font-mono">{w.payment_deadline}</TableCell>
                      <TableCell className="text-right font-mono">{fmtMoney(w.total_gross_minor, "MYR")}</TableCell>
                      <TableCell className="text-right font-mono">{fmtMoney(w.total_tax_minor, "MYR")}</TableCell>
                      <TableCell className="text-right font-mono">{w.line_count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
          <TabsContent value="fx">
            <div className="flex justify-end mb-3"><Button size="sm" onClick={() => setOpenFX(true)} data-testid="new-fx-button"><Plus className="w-4 h-4 mr-1.5" /> New rate</Button></div>
            <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Currency</TableHead><TableHead className="text-right">Rate → MYR</TableHead><TableHead>Session</TableHead><TableHead>Source</TableHead></TableRow></TableHeader>
                <TableBody>
                  {fx.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-6">No FX rates.</TableCell></TableRow>}
                  {fx.map((f) => (
                    <TableRow key={f.id}>
                      <TableCell className="text-xs font-mono">{fmtDate(f.date)}</TableCell>
                      <TableCell className="text-xs font-mono">{f.currency}</TableCell>
                      <TableCell className="text-right font-mono">{f.rate_to_myr.toFixed(4)}</TableCell>
                      <TableCell className="text-xs">{f.session}</TableCell>
                      <TableCell className="text-xs">{f.source}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </PageBody>

      <NewSSTDialog open={openSST} onOpenChange={setOpenSST} onCreated={() => { setOpenSST(false); load(); }} />
      <NewFXDialog open={openFX} onOpenChange={setOpenFX} onCreated={() => { setOpenFX(false); load(); }} />

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="min-w-[560px] overflow-y-auto">
          {selected && <SSTDetail worksheet={selected} onRefresh={async () => { const { data } = await api.get(`/tax/sst/${selected.id}`); setSelected(data); load(); }} />}
        </SheetContent>
      </Sheet>
    </>
  );
}

function NewSSTDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.period_start || !form.period_end || !form.filing_deadline || !form.payment_deadline) { toast.error("Fill all dates"); return; }
    setSaving(true);
    try { await api.post("/tax/sst", form); toast.success("Worksheet built"); onCreated(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>New SST worksheet</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase">Period start</Label><Input type="date" onChange={(e) => setForm({ ...form, period_start: e.target.value })} data-testid="sst-start" /></div>
            <div><Label className="text-xs uppercase">Period end</Label><Input type="date" onChange={(e) => setForm({ ...form, period_end: e.target.value })} data-testid="sst-end" /></div>
            <div><Label className="text-xs uppercase">Filing deadline</Label><Input type="date" onChange={(e) => setForm({ ...form, filing_deadline: e.target.value })} /></div>
            <div><Label className="text-xs uppercase">Payment deadline</Label><Input type="date" onChange={(e) => setForm({ ...form, payment_deadline: e.target.value })} /></div>
          </div>
          <div><Label className="text-xs uppercase">SST number</Label><Input onChange={(e) => setForm({ ...form, sst_number: e.target.value })} className="font-mono" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="sst-submit">{saving ? "Building…" : "Build worksheet"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewFXDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ currency: "SGD", session: "middle", source: "BNM", rate_to_myr: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.date || !form.rate_to_myr) { toast.error("Date and rate required"); return; }
    setSaving(true);
    try { await api.post("/tax/fx", { ...form, rate_to_myr: parseFloat(form.rate_to_myr) }); toast.success("Rate recorded"); onCreated(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Record FX rate</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs uppercase">Currency</Label>
            <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="SGD">SGD</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs uppercase">Date</Label><Input type="date" onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
          <div><Label className="text-xs uppercase">Rate → MYR</Label><Input type="number" step="0.0001" value={form.rate_to_myr} onChange={(e) => setForm({ ...form, rate_to_myr: e.target.value })} className="font-mono" data-testid="fx-rate" /></div>
          <div><Label className="text-xs uppercase">Session</Label>
            <Select value={form.session} onValueChange={(v) => setForm({ ...form, session: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="opening">Opening</SelectItem><SelectItem value="middle">Middle</SelectItem><SelectItem value="closing">Closing</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="fx-submit">{saving ? "Saving…" : "Record"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SSTDetail({ worksheet, onRefresh }) {
  const setStatus = async (field, value, reference) => {
    try { await api.post(`/tax/sst/${worksheet.id}/status`, { field, value, reference }); toast.success("Updated"); onRefresh(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };
  return (
    <>
      <SheetHeader><SheetTitle className="font-mono">{worksheet.period_start} → {worksheet.period_end}</SheetTitle></SheetHeader>
      <div className="mt-6 space-y-4 text-sm">
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div><div className="text-slate-500 uppercase">Filing due</div><div className="font-mono">{worksheet.filing_deadline}</div></div>
          <div><div className="text-slate-500 uppercase">Payment due</div><div className="font-mono">{worksheet.payment_deadline}</div></div>
          <div><div className="text-slate-500 uppercase">Gross</div><div className="font-mono">{fmtMoney(worksheet.total_gross_minor, "MYR")}</div></div>
          <div><div className="text-slate-500 uppercase">SST</div><div className="font-mono">{fmtMoney(worksheet.total_tax_minor, "MYR")}</div></div>
        </div>
        <div className="border-t border-slate-200 pt-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Statuses</div>
          {["review_status", "filing_status", "payment_status"].map((f) => (
            <div key={f} className="flex items-center gap-2 mb-2">
              <span className="text-xs text-slate-500 w-28">{f.replace("_status", "").toUpperCase()}</span>
              <span className="text-xs font-mono">{worksheet[f] || "pending"}</span>
              <div className="ml-auto flex gap-1">
                <Button size="sm" variant="outline" className="h-6 text-xs px-2" onClick={() => setStatus(f, "in_progress")}>In progress</Button>
                <Button size="sm" variant="outline" className="h-6 text-xs px-2 bg-emerald-50" onClick={() => setStatus(f, "done")}>Done</Button>
              </div>
            </div>
          ))}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Lines ({worksheet.line_count})</div>
          <div className="border rounded overflow-hidden max-h-96 overflow-y-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Sale</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Gross</TableHead><TableHead className="text-right">Tax</TableHead></TableRow></TableHeader>
              <TableBody>
                {worksheet.lines.map((l, i) => (
                  <TableRow key={`${l.sale_number || "line"}-${i}`}>
                    <TableCell className="text-xs font-mono">{l.sale_number}</TableCell>
                    <TableCell className="text-xs font-mono">{l.received_date}</TableCell>
                    <TableCell className="text-right font-mono">{fmtMoney(l.gross_minor, l.currency)}</TableCell>
                    <TableCell className="text-right font-mono">{fmtMoney(l.tax_minor, l.currency)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </>
  );
}
